import React from 'react';

import NotificationsIcon from 'icons/notifications.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import ModalBox from 'components/application/ModalBox';

import { formatDate, formatDistanceToNow } from 'components/helpers/dates';
import { resourceShape } from 'components/helpers/serialisableResources';

export default function BookingSourceReminderModal({
  bookingSource,
  course,
  domainPersonnelMeta,
  isOpen,
  onClose,
  onSubmit,
  personnel,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'bookings.BookingSourceReminderModal',
  });

  const { t: i18nTCommon } = useTranslation('common');

  return (
    <ModalBox
      customFooter={
        <div className='modalbox__footer-actions modalbox__footer-actions--right'>
          <TextButton
            className='tw-mr-3'
            color='blue'
            onClick={onClose}
            size='md'
          >
            {i18nTCommon('shared.cancel')}
          </TextButton>
          <FilledButton
            color='mint'
            onClick={() => onSubmit({ bookingSource })}
          >
            {bookingSource.type === 'bookingGroup' ?
              i18nT('bookingGroup.button', {
                count: domainPersonnelMeta?.notifiableSetCount,
              })
            : i18nTCommon('shared.sendReminder')}
          </FilledButton>
        </div>
      }
      isOpen={isOpen}
      mode='flexible'
      onClose={onClose}
    >
      <div className='tw-p-8'>
        {bookingSource.type === 'booking' && (
          <>
            <h2 className='tw-m-0 tw-mb-6 tw-p-0 tw-text-l tw-font-semibold tw-text-grey-900'>
              {i18nT('booking.title', {
                courseName: course.attributes.name,
                interpolation: { escapeValue: false },
                personnelName: personnel.attributes.firstName,
              })}
            </h2>
            <p className='tw-m-0 tw-p-0 tw-text-m tw-font-normal tw-text-grey-700'>
              {i18nTCommon('personnel.emailNotifications.personnel')}
            </p>
          </>
        )}
        {bookingSource.type === 'bookingGroup' && (
          <>
            <h2 className='tw-m-0 tw-mb-6 tw-p-0 tw-text-l tw-font-semibold tw-text-grey-900'>
              {i18nT('bookingGroup.title')}
            </h2>
            <p className='tw-m-0 tw-p-0 tw-text-m tw-font-normal tw-text-grey-700'>
              {`${i18nTCommon('shared.course')}: `}
              <span className='tw-m-0 tw-p-0 tw-text-m tw-font-medium tw-text-grey-700'>
                {course.attributes.name}
              </span>
            </p>
            <p className='tw-m-0 tw-p-0 tw-text-m tw-font-normal tw-text-grey-700'>
              {`${i18nTCommon('training.trainingDate')}: `}
              <span className='tw-m-0 tw-p-0 tw-text-m tw-font-medium tw-text-grey-700'>
                {formatDate({
                  date: bookingSource.attributes.date,
                  format: 'd MMM yyyy',
                })}
              </span>
            </p>
            <br />
            <p className='tw-m-0 tw-p-0 tw-text-m tw-font-normal tw-text-grey-700'>
              {i18nT('bookingGroup.description')}
            </p>
            {bookingSource.attributes.notifiedAt && (
              <div className='tw-mt-6 tw-bg-cyan-025'>
                <div className='tw-flex tw-items-center tw-p-3'>
                  <div className='tw-relative tw-top-0.5'>
                    <NotificationsIcon
                      className='[&_path]:tw-fill-cyan-800'
                      height={16}
                      width={12}
                    />
                  </div>
                  <div className='tw-ml-4'>
                    <p className='tw-m-0 tw-p-0 tw-font-inter tw-font-normal tw-text-cyan-800'>
                      {i18nT('bookingGroup.details.lastReminded')}{' '}
                      <span className='tw-font-medium'>
                        {formatDistanceToNow({
                          date: bookingSource.attributes.notifiedAt,
                        })}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </ModalBox>
  );
}

BookingSourceReminderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  bookingSource: resourceShape(['booking', 'bookingGroup']).isRequired,
  course: resourceShape('course').isRequired,
  personnel: resourceShape('personnel'),
  domainPersonnelMeta: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
};
